import { Sync, SyncSource, SyncSourceDetails, SyncState } from "./ResearchDataModel"
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { MetricData, metricTotal } from '@webapp/util/Metrics'
import { useWorkspaceStore } from './WorkspaceStore'
import { uniqBy } from 'lodash'

export const useSyncStore = defineStore("syncStore", () => {
    const asyncSyncs = new Map<string, AsyncData<Sync>>()

    const rd = useResearchDataApi()
    const workspaceStore = useWorkspaceStore()
    
    function getAsyncSync(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncSyncList([id], loadMode)[0] : undefined
    }

    function getAsyncSyncList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useSyncStore() // use to get/return reactive store objects
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainByIdsAsync("syncs", idsToLoad)
            return processExpandedSyncs(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncSyncs, ids, "Sync", loadMode, loadAsync)
    }

    function getAsyncKeyListForWorkspace(workspaceId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useSyncStore() // use to get/return reactive store objects
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainCollectionByIdsAsync(id => `workspaces/${id}/syncs`, idsToLoad)
            return processExpandedSyncs(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncKeyListForRelatedItems(workspaceId, "Workspace", workspaceStore.asyncSyncKeys, 
            store.asyncSyncs, "Sync", loadMode, loadAsync)
    }

    function getLoadedSyncs(ids: (string | undefined)[]) {
        return getAsyncSyncList(ids, LoadMode.TrackOnly).map(a => a.data).filter(isDefined)
    }

    function addSyncsToStore(plainSyncs: any[], requestTime: DateTime) {
        const store = useSyncStore() // use to get/return reactive store objects
        const syncMap = processExpandedSyncs(uniqBy(plainSyncs, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncSyncs, "Sync", syncMap, requestTime)
    }

    function processExpandedSyncs(plainSyncs: any[], requestTime: DateTime) {
        return AsyncDataStore.createItemMap(plainSyncs, "Sync", plainToSync, s => s.id!)
    }

    async function addAsync(sync: Sync) {
        const requestTime = DateTime.utc()
        const p = await rd.postPlainAsync('syncs', sync)
        addSyncsToStore([p], requestTime)
        return plainToSync(p)
    }

    async function cancelAsync(id: string) {
        await rd.postPlainAsync(`syncs/${id}/cancel`)
        await getAsyncSync(id, LoadMode.Reload)?.whenLoadCompleted
    }

    async function getSyncCountAsync() {
        return metricTotal(await rd.getPlainAsync("metrics/syncs") as MetricData[])
    }

    return {
        asyncSyncs,

        getAsyncSync,
        getAsyncSyncList,
        getLoadedSyncs,
        getAsyncKeyListForWorkspace,

        addSyncsToStore,
        processExpandedSyncs,

        addAsync,
        cancelAsync,
        getSyncCountAsync,
    }
})

function plainToSync(p: any) {
    const s = assignExisting(new Sync(), p)
    s.state ??= SyncState.Active
    s.createdDate = DateTimeUtil.fromAPI(p.createdDate)
    if (p.completedDate) {
        s.completedDate = DateTimeUtil.fromAPI(p.completedDate)
    }
    if (p.source) {
        s.source = assignExisting(new SyncSource(), p.source)
        s.source.modifiedDate = DateTimeUtil.fromAPI(p.source.modifiedDate)
    }
    if (p.sourceDetails) {
        s.sourceDetails = assignExisting(new SyncSourceDetails(), p.sourceDetails)
    }
    return s;
}
